'use client';

import SparkMD5 from 'spark-md5';
import {
  cleanSmartQuotes,
  cleanUnicodeText,
  normalizeLineBreaks,
  removeExtraSpaces,
} from '@/lib/browser-text-fix-tools';

export type PrivacyFindingType = 'email' | 'phone' | 'url' | 'ip' | 'id' | 'token' | 'secret-key';

export type PrivacyFinding = {
  path: string;
  key: string | null;
  type: PrivacyFindingType;
  preview: string;
};

export type JsonPiiScanResult = {
  findings: PrivacyFinding[];
  redactedJson: string;
  findingsJson: string;
};

export type FileHashResult = {
  fileName: string;
  size: number;
  sha256: string;
  sha512: string;
  sha1: string;
  md5: string;
  crc32: string;
  matchedAlgorithm: 'sha256' | 'sha512' | 'sha1' | 'md5' | 'crc32' | null;
};

export type EmailRedactionMode = 'placeholder' | 'mask-local';
export type PhoneRedactionMode = 'placeholder' | 'mask-last-4' | 'mask-last-2';

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const URL_PATTERN = /\bhttps?:\/\/[^\s<>"')]+/gi;
const IPV4_PATTERN = /\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/g;
const LONG_ID_PATTERN = /\b\d{7,}\b/g;
const TOKEN_PATTERN = /\b[a-z0-9_-]{24,}\b/gi;
const PHONE_PATTERN = /(?<!\w)(?:\+?\d[\d()\-\s]{6,}\d)(?!\w)/g;
const SECRET_KEY_PATTERN =
  /(password|passwd|secret|token|api[-_]?key|access[-_]?token|auth[-_]?token|private[-_]?key)/i;

function maskEmailLocal(email: string) {
  const [local, domain] = email.split('@');
  if (!local || !domain) {
    return '[email]';
  }

  if (local.length <= 2) {
    return `${local.charAt(0) ?? '*'}*@${domain}`;
  }

  return `${local.charAt(0)}${'*'.repeat(Math.max(2, local.length - 2))}${local.slice(-1)}@${domain}`;
}

function maskPhone(value: string, visibleDigits: number) {
  const digitIndexes = Array.from(value)
    .map((char, index) => ({ char, index }))
    .filter((entry) => /\d/.test(entry.char));

  const keepFrom = Math.max(0, digitIndexes.length - visibleDigits);
  const visibleIndexSet = new Set(digitIndexes.slice(keepFrom).map((entry) => entry.index));

  return Array.from(value)
    .map((char, index) => {
      if (!/\d/.test(char)) {
        return char;
      }

      return visibleIndexSet.has(index) ? char : '*';
    })
    .join('');
}

function cleanPreview(value: string, maxLength = 72) {
  const compact = value.replace(/\s+/g, ' ').trim();
  if (compact.length <= maxLength) {
    return compact;
  }

  return `${compact.slice(0, maxLength - 1)}…`;
}

function normalizeClipboardText(value: string) {
  return removeExtraSpaces(normalizeLineBreaks(cleanSmartQuotes(cleanUnicodeText(value)), 'lf'));
}

function addFinding(
  findings: PrivacyFinding[],
  path: string,
  key: string | null,
  type: PrivacyFindingType,
  preview: string,
) {
  findings.push({
    path,
    key,
    type,
    preview: cleanPreview(preview),
  });
}

function detectValueType(value: string): PrivacyFindingType | null {
  if (EMAIL_PATTERN.test(value)) {
    EMAIL_PATTERN.lastIndex = 0;
    return 'email';
  }
  EMAIL_PATTERN.lastIndex = 0;

  if (PHONE_PATTERN.test(value)) {
    PHONE_PATTERN.lastIndex = 0;
    return 'phone';
  }
  PHONE_PATTERN.lastIndex = 0;

  if (URL_PATTERN.test(value)) {
    URL_PATTERN.lastIndex = 0;
    return 'url';
  }
  URL_PATTERN.lastIndex = 0;

  if (IPV4_PATTERN.test(value)) {
    IPV4_PATTERN.lastIndex = 0;
    return 'ip';
  }
  IPV4_PATTERN.lastIndex = 0;

  if (LONG_ID_PATTERN.test(value)) {
    LONG_ID_PATTERN.lastIndex = 0;
    return 'id';
  }
  LONG_ID_PATTERN.lastIndex = 0;

  if (TOKEN_PATTERN.test(value)) {
    TOKEN_PATTERN.lastIndex = 0;
    return 'token';
  }
  TOKEN_PATTERN.lastIndex = 0;

  return null;
}

function redactValue(type: PrivacyFindingType, value: string) {
  switch (type) {
    case 'email':
      return '[email]';
    case 'phone':
      return '[phone]';
    case 'url':
      return '[url]';
    case 'ip':
      return '[ip]';
    case 'id':
      return '[id]';
    case 'token':
    case 'secret-key':
      return '[secret]';
  }
}

function walkJsonValue(
  value: unknown,
  path: string,
  findings: PrivacyFinding[],
  keyName: string | null,
): unknown {
  if (Array.isArray(value)) {
    return value.map((entry, index) => walkJsonValue(entry, `${path}[${index}]`, findings, null));
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(record).map(([key, entry]) => {
        const nextPath = path === '$' ? `$.${key}` : `${path}.${key}`;
        const suspiciousKey = SECRET_KEY_PATTERN.test(key);
        if (suspiciousKey) {
          addFinding(findings, nextPath, key, 'secret-key', String(entry ?? ''));
          return [key, redactValue('secret-key', String(entry ?? ''))];
        }

        return [key, walkJsonValue(entry, nextPath, findings, key)];
      }),
    );
  }

  if (typeof value === 'string') {
    const detectedType = detectValueType(value);
    if (detectedType) {
      addFinding(findings, path, keyName, detectedType, value);
      return redactValue(detectedType, value);
    }
  }

  if (typeof value === 'number') {
    const text = String(value);
    if (LONG_ID_PATTERN.test(text)) {
      LONG_ID_PATTERN.lastIndex = 0;
      addFinding(findings, path, keyName, 'id', text);
      return '[id]';
    }
    LONG_ID_PATTERN.lastIndex = 0;
  }

  return value;
}

export function anonymizeText(value: string) {
  return value
    .replace(URL_PATTERN, '[url]')
    .replace(EMAIL_PATTERN, '[email]')
    .replace(PHONE_PATTERN, '[phone]')
    .replace(IPV4_PATTERN, '[ip]')
    .replace(LONG_ID_PATTERN, '[id]')
    .replace(TOKEN_PATTERN, '[id]');
}

export function redactEmails(value: string, mode: EmailRedactionMode) {
  return value.replace(EMAIL_PATTERN, (match) =>
    mode === 'mask-local' ? maskEmailLocal(match) : '[email]',
  );
}

export function redactPhoneNumbers(value: string, mode: PhoneRedactionMode) {
  if (mode === 'placeholder') {
    return value.replace(PHONE_PATTERN, '[phone]');
  }

  const visibleDigits = mode === 'mask-last-2' ? 2 : 4;
  return value.replace(PHONE_PATTERN, (match) => maskPhone(match, visibleDigits));
}

export function cleanClipboardText(value: string) {
  return normalizeClipboardText(value);
}

export function scanJsonPii(value: string): JsonPiiScanResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(value);
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'The JSON could not be parsed.');
  }

  const findings: PrivacyFinding[] = [];
  const redacted = walkJsonValue(parsed, '$', findings, null);

  return {
    findings,
    redactedJson: JSON.stringify(redacted, null, 2),
    findingsJson: JSON.stringify(findings, null, 2),
  };
}

async function digestHex(buffer: ArrayBuffer, algorithm: 'SHA-256' | 'SHA-512' | 'SHA-1') {
  const hashBuffer = await crypto.subtle.digest(algorithm, buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

// IEEE 802.3 CRC-32, the variant zip, crc32(1) and every download page mean by
// "CRC32". Table-based, one pass over the buffer.
const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);

  for (let i = 0; i < 256; i += 1) {
    let value = i;

    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }

    table[i] = value >>> 0;
  }

  return table;
})();

function crc32Hex(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let crc = 0xffffffff;

  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }

  return ((crc ^ 0xffffffff) >>> 0).toString(16).padStart(8, '0');
}

export async function hashLocalFile(file: File, compareHash?: string): Promise<FileHashResult> {
  const buffer = await file.arrayBuffer();
  const [sha256, sha512, sha1] = await Promise.all([
    digestHex(buffer, 'SHA-256'),
    digestHex(buffer, 'SHA-512'),
    digestHex(buffer, 'SHA-1'),
  ]);
  const md5 = SparkMD5.ArrayBuffer.hash(buffer);
  const crc32 = crc32Hex(buffer);
  const normalizedCompare = compareHash?.trim().toLowerCase() ?? '';
  const matchedAlgorithm =
    normalizedCompare.length > 0
      ? sha256 === normalizedCompare
        ? 'sha256'
        : sha512 === normalizedCompare
          ? 'sha512'
          : sha1 === normalizedCompare
            ? 'sha1'
            : md5 === normalizedCompare
              ? 'md5'
              : crc32 === normalizedCompare.replace(/^0x/, '').padStart(8, '0')
                ? 'crc32'
                : null
      : null;

  return {
    fileName: file.name,
    size: file.size,
    sha256,
    sha512,
    sha1,
    md5,
    crc32,
    matchedAlgorithm,
  };
}

export function createPrivacyJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}
